import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { FaaReportOptions } from '../data-models/faa-report-options.model';
import { FaaReport } from '../data-models/faa-report.model';
import { FaaReportBatchRequest } from '../request-models/faa-report-batch-request.model';
import { FaaReportFilters } from '../request-models/faa-report-filters.model';
import { MissingFlightCountsRequest } from '../request-models/missing-flight-counts-request.model';
import { ReportDatesRequest } from '../request-models/report-dates-request.model';
import { BatchMissingCountSummary } from '../response-models/batch-missing-count-summary.model';
import { FaaReportWithJobs } from '../response-models/faa-report-with-jobs.model';
import { Page } from '../response-models/page.model';
import { QueueStats } from '../response-models/queue-stats.model';
import { ReportBatchResult } from '../response-models/report-batch-result.model';
import { isRecord } from '../utils/is-record';
import { toMissingCountSummary, toQueueStats, toReportBatchResult, toReportJob } from '../utils/report-wire-utils';
import { listOf } from '../utils/wire-value-utils';

/**
 * FAA reports across all organizations: `/admin/faa-reports` (hub `admin-faa-reports-controller.ts`), paged with
 * `limit`. Failures reject with an `ApiError`.
 */
@Injectable({ providedIn: 'root' })
export class FaaReportService extends BaseService<FaaReport> {
  constructor() {
    super('admin/faa-reports', FaaReport);
  }

  /** GET /admin/faa-reports: one page of reports (`filters.pageSize` is sent as `limit`). */
  listReports(filters: FaaReportFilters = {}): Promise<Page<FaaReport>> {
    const query = {
      page: filters.page,
      limit: filters.pageSize,
      organizationId: filters.organizationId || undefined,
      status: filters.status || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      search: filters.search?.trim() || undefined,
    };
    return firstValueFrom(this.getPage({ query }));
  }

  /** GET /admin/faa-reports/:id: the report with its download URL (when ready) and generation jobs. */
  getReport(reportId: string): Promise<FaaReportWithJobs | null> {
    return firstValueFrom(this.request('GET', { id: reportId }, toReportWithJobs));
  }

  /** DELETE /admin/faa-reports/:id (soft delete; refused while generating). */
  deleteReport(reportId: string): Promise<void> {
    return firstValueFrom(this.delete(reportId));
  }

  /** PUT /admin/faa-reports/:id: the only writable fields are the two dates. */
  updateReportDates(reportId: string, startDate: string, endDate: string): Promise<FaaReport | null> {
    const body: ReportDatesRequest = { startDate, endDate };
    return firstValueFrom(this.update(reportId, body));
  }

  /** POST /admin/faa-reports/:id/regenerate: queues a new generation job. */
  regenerateReport(reportId: string): Promise<void> {
    return firstValueFrom(this.request('POST', { id: reportId, path: 'regenerate' }, () => undefined, {}));
  }

  /** POST /admin/faa-reports/:id/retry: retries a failed generation job. */
  retryReport(reportId: string): Promise<void> {
    return firstValueFrom(this.request('POST', { id: reportId, path: 'retry' }, () => undefined, {}));
  }

  /** GET /admin/faa-reports/queue-stats */
  getQueueStats(): Promise<QueueStats> {
    return firstValueFrom(this.request('GET', { path: 'queue-stats' }, toQueueStats));
  }

  /**
   * POST /admin/faa-reports/missing-flight-counts: the aggregate missing-flight-count warning (FAA §F.2.d) for
   * the batch confirm modal. Advisory only: resolves to `null` on any failure, so a check that cannot be computed
   * never stops an operator filing (an under-count is a compliance problem; being unable to file is worse).
   */
  async fetchMissingFlightCounts(organizationIds: string[], startDate: string, endDate: string): Promise<BatchMissingCountSummary | null> {
    const body: MissingFlightCountsRequest = { organizationIds, startDate, endDate };
    try {
      return await firstValueFrom(this.request('POST', { path: 'missing-flight-counts' }, toMissingCountSummary, body));
    } catch (error) {
      console.error('Error fetching missing flight counts:', error);
      return null;
    }
  }

  /** POST /admin/faa-reports/batch: creates and queues one report per organization. */
  generateBatch(
    organizationIds: string[],
    startDate: string,
    endDate: string,
    options: FaaReportOptions
  ): Promise<ReportBatchResult<FaaReport>> {
    const body: FaaReportBatchRequest = { organizationIds, startDate, endDate, options };
    return firstValueFrom(this.request('POST', { path: 'batch' }, data => toReportBatchResult(data, toReport), body));
  }
}

function toReport(data: unknown): FaaReport {
  return new FaaReport(data);
}

function toReportWithJobs(data: unknown): FaaReportWithJobs | null {
  if (!isRecord(data) || !isRecord(data['report'])) {
    return null;
  }
  return Object.assign(new FaaReport(data['report']), { jobs: listOf(data['jobs'], toReportJob) });
}
